//----------------------------------
// interfaces
//----------------------------------
export interface PendingWeeklyOff {
    name?: string;
    type?: "branch" | "department" | "employee";
    selectedTypeId?: string[];
    noOfDays?: number;
    weekDays?: string[];
    weekPattern?: string;
    description?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteWeeklyOff {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE WEEKLY OFF — START
// -------------------------------
export const startCreateWeeklyOffFlow = (
    setPending: (data: PendingWeeklyOff | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ name: nameFromIntent });

        pushAIChat(`Great! For "${nameFromIntent}", is this for Branch, Department, or Employee?`);
        speak(`Is this weekly off for branch, department, or employee?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the Weekly Off name be?");
    speak("What should the weekly off name be?");
};

// -------------------------------
// CREATE WEEKLY OFF — PROCESS
// -------------------------------
export const processPendingWeeklyOff = (
    text: string,
    pending: PendingWeeklyOff,
    setPending: (data: PendingWeeklyOff | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Name
    if (!pending.name) {
        const name = text.trim();
        if (!name) {
            pushAIChat("Please provide a valid Weekly Off name.");
            speak("Please provide a valid weekly off name.");
            return;
        }

        setPending({ ...pending, name });
        pushAIChat("Is this Weekly Off for Branch, Department, or Employee?");
        speak("Is this for branch, department, or employee?");
        return;
    }

    // STEP 2 — Type
// STEP 2 — Type
if (!pending.type) {
  const lower = text.toLowerCase();

  let type: "branch" | "department" | "employee" | null = null;

  if (lower.includes("branch")) type = "branch";
  if (lower.includes("department")) type = "department";
  if (lower.includes("employee")) type = "employee";

  if (!type) {
    pushAIChat("Please say Branch, Department, or Employee.");
    speak("Please say branch, department, or employee.");
    return;
  }

  setPending({ ...pending, type });

  pushAIChat(`Please select ${type}(s) from the list below.`);
  speak(`Please select ${type}`);

  // 🔥 FIRE SUGGESTION EVENT
  window.dispatchEvent(
    new CustomEvent("ai-suggest-options", {
      detail: { type }
    })
  );

  return;
}


    // STEP 3 — Number of Days
    if (!pending.noOfDays) {
        const days = parseInt(text, 10);

        if (isNaN(days) || days <= 0) {
            pushAIChat("Please provide a valid number of days.");
            speak("Please provide a valid number of days.");
            return;
        }

        setPending({ ...pending, noOfDays: days });

        pushAIChat("Which weekdays? (Example: Sunday or Saturday, Sunday)");
        speak("Which weekdays?");
        return;
    }

    // STEP 4 — Week Days
    if (!pending.weekDays) {
        const weekDays = text
            .split(",")
            .map(d => d.trim())
            .filter(Boolean);

        if (!weekDays.length) {
            pushAIChat("Please provide valid weekdays.");
            speak("Please provide valid weekdays.");
            return;
        }

        setPending({ ...pending, weekDays });

        pushAIChat("What is the week pattern? (Every week / Alternate week)");
        speak("What is the week pattern?");
        return;
    }

    // STEP 5 — Week Pattern
    if (!pending.weekPattern) {
        setPending({ ...pending, weekPattern: text });

        pushAIChat("Would you like to add a description?");
        speak("Would you like to add a description?");
        return;
    }

    // STEP 6 — Description
    if (!pending.description) {
        setPending({ ...pending, description: text });

        pushAIChat("Should this Weekly Off be active or inactive?");
        speak("Should this weekly off be active or inactive?");
        return;
    }

    // STEP 7 — Status (FINAL)
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        setPending({ ...pending, status });

        pushAIChat(`Creating Weekly Off "${pending.name}"...`);
        speak(`Creating weekly off ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-weeklyoff", {
                detail: {
                    name: pending.name,
                    type: pending.type,
                    selectedTypeId: pending.selectedTypeId || [],
                    noOfDays: pending.noOfDays,
                    weekDays: pending.weekDays,
                    weekPattern: pending.weekPattern,
                    description: pending.description,
                    status
                }
            })
        );

        setPending(null);
        return;
    }
};

// -------------------------------
// DELETE WEEKLY OFF — START
// -------------------------------
export const startDeleteWeeklyOffFlow = (
    setPendingDelete: (data: PendingDeleteWeeklyOff | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting Weekly Off "${nameFromIntent}"...`);
        speak(`Deleting weekly off ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-weeklyoff", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which Weekly Off would you like to delete?");
    speak("Which weekly off would you like to delete?");
};

// -------------------------------
// DELETE WEEKLY OFF — PROCESS
// -------------------------------
export const processPendingDeleteWeeklyOff = (
    text: string,
    pending: PendingDeleteWeeklyOff,
    setPendingDelete: (data: PendingDeleteWeeklyOff | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const name = text.trim();

        if (!name) {
            pushAIChat("Please provide a valid Weekly Off name.");
            speak("Please provide a valid weekly off name.");
            return;
        }

        pushAIChat(`Deleting Weekly Off "${name}"...`);
        speak(`Deleting weekly off ${name}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-weeklyoff", {
                detail: { name }
            })
        );

        setPendingDelete(null);
    }
};